import _, {isArray, isEqual, isFunction, pickBy} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';

import {isHtmlElement} from '../../../libs/shared/helpers/src/browser/domHelpers';
import {EventHandler, HandlerOf, Unregistrator} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {isInteger, isPromise} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useConstant, useDeepMemo} from '../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {
  ApplicationMessageTypesEnum,
  ApplicationResultMessage,
  ApplicationResultMessageTypesEnum,
} from '../../applications/bridge/v2/applicationBridgeV2';
import {ApplicationContext} from '../../applications/bridge/v2/types/bridgeTypesV2';
import {
  FrontEngineHandshakeModel,
  LegacyApplicationRuntimeMessageTypesEnum,
} from '../../applications/runtime/legacyApplicationRuntimeHelpers';
import {IOTSError, readApiModel} from '../../helpers/apiHelpers';
import {buildCancelTokenSource, CancelTokenSource} from '../../helpers/asyncCancelHelpers';
import {
  buildLegacyAppEngineIframeName,
  buildLegacyAppEngineIframeUrl,
  buildLegacyAppIframeFeaturePolicies,
  buildLegacyApplicationIframeOrigin,
  buildLegacyApplicationLogger,
  buildLegacyAppPreloadViewIframeName,
  buildLegacyAppViewIframeUrl,
  legacyAppIframePermissions,
} from '../../helpers/models/legacyApplicationHelpers';
import {LegacyApplicationModel} from '../../models/api/legacyApplicationModel';
import {LegacyEntryPointModel} from '../../models/api/legacyEntryPointModel';
import {
  ApiLegacyApplicationMessage,
  legacyApplicationContextFunctionArities,
  LegacyApplicationFunctionCallMessageModel,
  LegacyApplicationFunctionCancelMessageModel,
} from '../../models/applications/legacyApplicationMessageModel';
import {LegacyEntryPointTemplateModel} from '../../models/applications/legacyEntryPointTemplateModel';
import {trackEvent, TrackingTypesEnum} from '../../services/tracking';
import {Iframe} from '../common/iframe';

/*
 * Props.
 */

export type SendContext = (context: ApplicationContext) => Unregistrator;
export type SendContextRegistrator = (applicationId: number, sendContext: SendContext) => Unregistrator;

interface LegacyApplicationEngineProps {
  application: LegacyApplicationModel;
  registerSendContext: SendContextRegistrator;
}

type LegacyAppContextMessageModel =
  | LegacyApplicationFunctionCallMessageModel
  | LegacyApplicationFunctionCancelMessageModel;

/*
 * Component.
 */

export const LegacyApplicationEngine: FC<LegacyApplicationEngineProps> = (props) => {
  const {application, registerSendContext} = props;
  const contexts = useConstant(() => new Map<string, ApplicationContext>());
  const contextFunctionCancelTokenSources = useConstant(() => new Map<string, CancelTokenSource>());
  const [iframePort, setIframePort] = useState<MessagePort>();
  const [sendContext, setSendContext] = useState<SendContext>();

  const {id: applicationId, entryPoints} = application;
  const entryPointTemplates = useDeepMemo(() => entryPoints.map(entryPointToTemplate), [entryPoints]);
  const entryPointsString = useMemo(() => JSON.stringify(entryPointTemplates), [entryPointTemplates]);

  /*
   * Logging.
   */

  const log = useMemo(() => buildLegacyApplicationLogger(application.name), [application.name]);

  const trackApiUsage = useCallback(
    (name: string) => {
      trackEvent(TrackingTypesEnum.UI_SDK_API, name, {application: application.typeId});
    },
    [application.typeId],
  );

  /*
   * Frame message handling.
   */

  // When the iframe is loaded, establish the MessageChannel link.
  const onIframeLoad: EventHandler<HTMLIFrameElement> = ({currentTarget}) => {
    if (!currentTarget || !isHtmlElement(currentTarget) || !currentTarget.contentWindow) {
      return;
    }

    const handshakeMessage: FrontEngineHandshakeModel = {
      type: LegacyApplicationRuntimeMessageTypesEnum.HANDSHAKE_ENGINE,
      bundleUrl: application.bundleUrl,
    };

    const {port1, port2} = new MessageChannel();
    const targetOrigin = buildLegacyApplicationIframeOrigin(application);
    currentTarget.contentWindow.postMessage(handshakeMessage, targetOrigin, [port2]);
    setIframePort(port1);
    log('Engine handshake sent.', handshakeMessage);
  };

  const sendMessageToPort = useCallback(
    (message: ApplicationResultMessage) => {
      if (!iframePort) {
        return;
      }

      iframePort.postMessage(message);
      log('Message sent.', message);
    },
    [iframePort, log],
  );

  const onPortContextFunctionMessage: HandlerOf<LegacyAppContextMessageModel> = useCallback(
    async (message) => {
      const sendResult = (result: unknown) =>
        sendMessageToPort({
          id: message.id,
          type: ApplicationResultMessageTypesEnum.FUNCTION_SUCCESS,
          value: result,
        });

      const sendError = (error: unknown) =>
        sendMessageToPort({
          id: message.id,
          type: ApplicationResultMessageTypesEnum.FUNCTION_ERROR,
          error: String(error),
        });

      switch (message.type) {
        case ApplicationMessageTypesEnum.FUNCTION_CALL: {
          const newCancelTokenSource = buildCancelTokenSource();
          contextFunctionCancelTokenSources.set(message.id, newCancelTokenSource);
          try {
            // Find the definition of the requested function.
            const functionDefinition = _(message.call).toPairs().first();

            if (!functionDefinition || !isArray(functionDefinition[1])) {
              throw new Error('No function definition was provided.');
            }

            // Find the context to call this function on.
            const context = contexts.get(message.contextId);
            if (!context) {
              throw new Error('No context found with this ID.');
            }

            // At this point, we have to trust that the function exists.
            const [functionName, functionRawArguments] = functionDefinition;
            const functionArguments = [...functionRawArguments.slice(0, -1), newCancelTokenSource.token];
            trackApiUsage(functionName);

            const result = (context as any)[functionDefinition[0]](...functionArguments);

            // If a promise was returned, await to return the result.
            if (isPromise(result)) {
              const promiseResult = await result;
              sendResult(promiseResult);
              return;
            }

            // Otherwise, return the result directly.
            sendResult(result);
          } catch (error) {
            sendError(error);
          } finally {
            contextFunctionCancelTokenSources.delete(message.id);
          }
          return;
        }

        case ApplicationMessageTypesEnum.FUNCTION_CANCEL: {
          const cancelTokenSource = contextFunctionCancelTokenSources.get(message.id);
          if (!cancelTokenSource) {
            return;
          }

          cancelTokenSource.cancel();
          return;
        }

        default:
          sendError(new Error('This should not happen.'));
      }
    },
    [contextFunctionCancelTokenSources, contexts, sendMessageToPort, trackApiUsage],
  );

  // Then, listen for messages from the MessageChannel.
  const onPortMessage: HandlerOf<MessageEvent> = useCallback(
    (event) => {
      try {
        // Parse the message. If this fails, there's nothing we can do.
        const message = readApiModel(ApiLegacyApplicationMessage, event.data);
        log('Message received.', message);

        switch (message.type) {
          case ApplicationMessageTypesEnum.INITIALIZE: {
            // Validate that all of the provided entry points match their definition.
            const lengthsMatch = message.entryPoints.length === entryPointTemplates.length;
            const contentsMatch = message.entryPoints.every((template1) =>
              entryPointTemplates.some((template2) => isEqual(template1, template2)),
            );

            if (!lengthsMatch || !contentsMatch) {
              throw new Error('Entry point mismatch.');
            }

            // Create the "send context" functions for these entry points.
            const newSendContext: SendContext = (context) => {
              // Keep track of the context for return calls.
              const contextId = context.id;
              contexts.set(contextId, context);

              // List the function names and their arity to include in the context.
              const functionArities = _(context)
                .mapValues((v, k) => isFunction(v) && legacyApplicationContextFunctionArities[k])
                .pickBy(isInteger)
                .value();

              // Send the context to the port.
              sendMessageToPort({
                type: ApplicationResultMessageTypesEnum.CONTEXT,
                context: {
                  ...pickBy(context, (p) => !isFunction(p)),
                  functionArities,
                } as ApplicationContext,
              });

              // Return a function to unregister the context.
              return () => {
                // Keep the context alive for a few ms in case the application already sent a request for it.
                setTimeout(() => contexts.delete(contextId), 250);
              };
            };

            setSendContext(() => newSendContext);
            return;
          }

          case ApplicationMessageTypesEnum.FUNCTION_CALL:
          case ApplicationMessageTypesEnum.FUNCTION_CANCEL: {
            onPortContextFunctionMessage(message);
            return;
          }

          default:
            throw new Error('This should not happen.');
        }
      } catch (error) {
        // If a function_call fails for an IO-TS error, return the error back.
        const {id, type} = event.data;
        if (error instanceof IOTSError && id && type === ApplicationMessageTypesEnum.FUNCTION_CALL) {
          sendMessageToPort({
            id,
            type: ApplicationResultMessageTypesEnum.FUNCTION_ERROR,
            error: String(error),
          });
          return;
        }

        reportToBugsnag(
          new ErrorWithData('Error processing application message.', {
            error,
            applicationId,
            data: event.data,
          }),
        );
      }
    },
    [log, entryPointTemplates, contexts, sendMessageToPort, onPortContextFunctionMessage, applicationId],
  );

  // TODO: This does not currently deal with a change in Bundle URL after initialization.
  useEffect(() => {
    if (!sendContext) {
      return undefined;
    }

    return registerSendContext(applicationId, sendContext);
  }, [registerSendContext, applicationId, sendContext]);

  useEffect(() => {
    if (!iframePort) {
      return undefined;
    }

    iframePort.addEventListener('message', onPortMessage);
    iframePort.start();
    log('Application connected.');

    return () => {
      iframePort.close();
      iframePort.removeEventListener('message', onPortMessage);
      log('Application disconnected.');
    };
  }, [iframePort, log, onPortMessage]);

  /*
   * Render.
   */

  return (
    <>
      <Iframe
        key={entryPointsString}
        title={`Application Engine — "${application.name}"`}
        name={buildLegacyAppEngineIframeName(application)}
        endpoint={buildLegacyAppEngineIframeUrl(application)}
        isVisible={false}
        onLoad={onIframeLoad}
        sandboxPermissions={legacyAppIframePermissions}
        featurePolicies={buildLegacyAppIframeFeaturePolicies(application)}
      />
      <Iframe
        title={`Application View Preload — "${application.name}"`}
        name={buildLegacyAppPreloadViewIframeName(application)}
        endpoint={buildLegacyAppViewIframeUrl(application)}
        isVisible={false}
        sandboxPermissions={legacyAppIframePermissions}
      />
    </>
  );
};

function entryPointToTemplate({id, type}: LegacyEntryPointModel): LegacyEntryPointTemplateModel {
  return {id, type};
}
